import { Injectable } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';

import { AddonPermissionService } from 'app/addon-permission/addon-permission.service';
import { AppService } from 'app/app.service';
import { AuthHelperService } from 'app/auth/helpers/auth-helper.service';
import { IdleUserDetectionService } from 'app/auth/helpers/idle-user-detection.service';
import { AuthenticationService } from 'app/auth/service';
import { RouterService } from 'app/global/router.service';
import { LocalStorageKeys, StorageService } from 'app/global/storage.service';
import { EventService } from 'app/main/pages/events/event.service';
import { SharedStoreFacade } from 'app/main/shared/shared/store/shared-store.facade';
import * as CategoryAction from 'app/store/actions/categories.actions';
import * as moment from 'moment';
import { ToastrService } from 'ngx-toastr';
import { combineLatest, filter, from, of, switchMap, timer } from 'rxjs';
import { catchError, delay, expand, map, mergeMap, take, takeUntil, tap } from 'rxjs/operators';
import { GlobalService } from '../../global/global.service';
import { LoginVia, UserSettings } from '../../global/user-settings.dto';
import * as AuthActions from '../actions/auth.action';

import * as UserActions from '../actions/user.actions';
import { ADD_USER_SETTINGS } from '../actions/user.actions';
import * as UserPermissionsActions from './../../global/permissions/store/permission.actions';
import * as AuthSelectors from './../selectors/auth.selectors';
import { IframeAbleService } from 'app/auth/helpers/iframeable.service';
import { HubspotService } from 'app/global/hubspot/hubspot.service';
import { UserSettingsFacade } from '../user-settings.facade';
import { StorageAccessManager } from 'app/utils/storage-access-manager.service';
import { StatusCodes } from 'app/auth/interceptors/status-codes';
import { translate } from '@jsverse/transloco';

@Injectable()
export class AuthEffects {
    readonly REFRESH_SESSION_BEFORE = 2 * 60;
    login$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AuthActions.login),

            tap(() => {
                this.store.dispatch(new UserActions.START_USER_INACTIVITY_DETECTION());
            }),

            mergeMap((action) => {
                // Convert only the await to an Observable
                return from(this._storageAccessManager.ensureStorageAccess()).pipe(
                    mergeMap(() => {
                        if (action.isThirdPartyLogin) {
                            this._routerService$.setCrmParams();
                        }

                        // switchLogin already returns Observable<Action>
                        return this.switchLogin(action);
                    })
                );
            })
        )
    );


    loginSucceeded$ = createEffect(() => {
        return this.actions$
            .pipe(
                ofType(AuthActions.loginSuccess),
                tap((action) => {
                    if (action?.userData?.invite_team_token) {
                        this._router.navigate(['onboarding/team-invitation', action?.userData?.invite_team_token]);
                        return;
                    }
                    else if (action?.userData?.onboarding === false) {
                        this._router.navigate(['onboarding', action?.userData?.token]);
                        return;
                    }
                    else if (action?.userData?.application_access === false) {
                        this._storageService$.addItem('trailPeriodCompleted', true);
                        this._router.navigate(['trail-period-completed']);
                        return;
                    }
                    else if (action?.userData?.is_2fa_enabled) {
                        this.authService.saveData(LocalStorageKeys.IS_2FA_ENABLED, 'true');
                        this._router.navigate(['verify-otp']);
                        return;
                    }
                    else {
                        this._sharedStoreFacade$.getDocumentTypes();
                        this.performAuthAction(action);
                    }
                })
            )
    }, { dispatch: false });

    loginFailed$ = createEffect(() => {
        return this.actions$
            .pipe(
                ofType(AuthActions.loginFailure),
                tap((message) => {
                    this.toaster.error(message.error, message.type, {
                        toastClass: 'toast ngx-toastr',
                        closeButton: true
                    });
                })
            )
    }, { dispatch: false });

    /**
     * Handle a loginBlocked action by persisting expiry and notifying all contexts.
     * Keeps side-effects out of switchLogin for clarity.
     */
    loginBlocked$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(AuthActions.loginBlocked),
            tap(({ expiresAt }) => {
                try {
                    localStorage.setItem(LocalStorageKeys.LOGIN_RETRY_EXPIRES_AT, String(expiresAt));
                } catch (e) { 
                    console.warn('Failed to set login retry state in localStorage:', e)
                 }

                // BroadcastChannel for cross-tab (and same-tab for modern browsers)
                try {
                    if (typeof BroadcastChannel !== 'undefined') {
                        const broadcastChannel = new BroadcastChannel('ss-login-retry');
                        broadcastChannel.postMessage({ expiresAt });
                        broadcastChannel.close();
                    }
                } catch (e) { console.warn('Failed to broadcast login retry state:', e); }
                // CustomEvent fallback to notify current tab immediately
                try {
                    if (typeof window !== 'undefined' && typeof CustomEvent !== 'undefined') {
                        window.dispatchEvent(new CustomEvent('ss:login-retry', { detail: { expiresAt } }));
                    }
                } catch (e) { console.warn('Failed to dispatch login retry custom event:', e); }
            })
        );
    }, { dispatch: false });

    stayLoggedIn$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(AuthActions.stayLoggedIn),
            tap(({ stayLoggedIn }) => {
                this._storageService$.addItem(LocalStorageKeys.STAY_LOGGED_IN, stayLoggedIn);
            })
        )
    }, {
        dispatch: false
    });

    onLoadAuthData$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(AuthActions.loadAuthData, AuthActions.loginSuccess, AuthActions.fetchUserDataSucceeded),
            switchMap(() => this.store.select(AuthSelectors.userDataSelector).pipe(take(1))),
            tap((userData) => {
                this.store.dispatch(UserPermissionsActions.refreshPermissionList(userData));
                this.store.dispatch(AuthActions.scheduleRefreshSession());
            })
        )
    }, {
        dispatch: false
    });

    logout$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(AuthActions.logout),
            switchMap(() => this.authService.logout()
                .pipe(
                    map(() => AuthActions.logoutSuccess()),
                    catchError((error) => of(AuthActions.logoutFail({ error: error })))
                )
            ),

        )
    })

    logoutSuccess$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(AuthActions.logoutSuccess),
            tap(() => {
                this.authService.logoutActions();
            })
        )
    }, { dispatch: false });

    hubSpotTrackingData$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(AuthActions.hubspotTrackingData),
            switchMap(() => {
                return combineLatest({
                    userData: this.userSettingsFacade._userSettings$.pipe(filter(userData => !!userData))
                }).pipe(take(1))
            }),
            switchMap(({ userData }) => {
                return this.authService.hubspotChat().pipe(
                    map((data: { token: string }) => {
                        this.hubspotService.loadTrackingScript(userData.email, data.token);
                        return AuthActions.hubspotTrackingDataSuccess();
                    }),
                    catchError((error) => of(AuthActions.hubspotTrackingDataFailure(error)))
                )
            })
        )
    });

    // Add more effects for other actions as needed

    constructor(
        private actions$: Actions,
        private authService: AuthenticationService,
        private authHelpder: AuthHelperService,
        private eventService: EventService,
        private appService: AppService,
        private store: Store,
        private _route: ActivatedRoute,
        private toaster: ToastrService,
        private _routerService$: RouterService,
        private _storageService$: StorageService,
        private _sharedStoreFacade$: SharedStoreFacade,
        private _globalService: GlobalService,
        private _addonPermissionService$: AddonPermissionService,
        private _store$: Store,
        private _router: Router,
        private _idleUserDetectionService: IdleUserDetectionService,
        private iframeAbleService: IframeAbleService,
        private hubspotService: HubspotService,
        private userSettingsFacade: UserSettingsFacade,
        private _storageAccessManager: StorageAccessManager
    ) {
    }

    /**
     * Handles the login process based on the provided action.
     *
     * @param action - The action object containing login details.
     * @returns An observable that emits either a login success or failure action.
     *
     * The function checks if the action contains an `end_user` property:
     * - If `end_user` is present, it calls `authService.doLogin` with the provided credentials.
     * - If `end_user` is not present, it calls `authService.loginMe`.
     *
     * Both scenarios map the response to a `loginSuccess` action with user data,
     * and handle errors by emitting a `loginFailure` action.
     */
    switchLogin(action) {
        if (action.end_user) {
            return this.authService.doLogin(action.credentials).pipe(
            map((data) => AuthActions.loginSuccess({
                userData: data,
                isThirdPartyLogin: action.isThirdPartyLogin,
                formData: action.credentials
            })),
            catchError(error => {
                // handle Retry-After for 401 / 429
                try {
                    if (error && (error.status === StatusCodes.UNAUTHORIZED || error.status === StatusCodes.TOO_MANY_REQUESTS)) {
                        const raw =
                            (error?.headers?.get?.('Retry-After')) ??
                            (error?.headers?.get?.('retry-after')) ??
                            null;
                        const seconds = this.parseRetryAfter(raw);
                         if (seconds && seconds > 0) {
                            const expiresAt = Date.now() + seconds * 1000;
                            const msg =  translate('Toaster error message for login (Login page) - Too many failed login attempts. Please try again in seconds', { seconds });
                            // Emit both actions: loginBlocked (side-effects handled by separate effect) and loginFailure (UI toast)
                            return of(
                                AuthActions.loginBlocked({ expiresAt, message: msg }),
                                AuthActions.loginFailure({ error: msg })
                            );
                        }
                    }
                } catch (e) {
                    console.warn('Failed to parse Retry-After header or dispatch loginBlocked action:', e);
                }

                let message = '';
                if (error?.error?.message) message = error.error.message;
                else if (typeof error === 'string') message = error;
                return of(AuthActions.loginFailure({ error: message }));
            }));
        } else {
            return this.authService.loginMe().pipe(
                map((data) => AuthActions.loginSuccess({
                    userData: { ...data, ...{ redirect_login: 1 } },
                    isThirdPartyLogin: action.isThirdPartyLogin,
                    formData: action.credentials
                })),
                catchError(error => of(AuthActions.loginFailure({ error }))))
        }
    }

    /**
     * Parse Retry-After header (seconds or HTTP-date).
     * Returns seconds number or null if unknown.
     */
    private parseRetryAfter(header: string | null): number | null {
        if (!header) return null;
        const raw = header.trim();

        // 1) integer seconds
        const sec = parseInt(raw, 10);
        if (!isNaN(sec)) {
            return Math.max(0, sec);
        }

        // 2) HTTP-date
        const ms = Date.parse(raw);
        if (!isNaN(ms)) {
            const deltaSec = Math.ceil((ms - Date.now()) / 1000);
            return deltaSec > 0 ? deltaSec : 0;
        }

        // unknown
        return null;
    }

    fetchUserData$ = createEffect(() => {
        return this.actions$
            .pipe(
                ofType(AuthActions.fetchUserData),
                switchMap(() => this.authService.loginMe()
                    .pipe(
                        map(userData => AuthActions.fetchUserDataSucceeded({ userData })),
                        catchError(error => of(AuthActions.fetchUserDataFailed({ error })))
                    )
                )
            )
    });



    verifyOtp$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(AuthActions.verifyOtp),
            switchMap(action => this.authService.verifyOtp({ otp: action.otp })
                .pipe(
                    map(userData => AuthActions.verifyOtpSuccess(userData)),
                    catchError(error => of(AuthActions.verifyOtpFailure(error)))
                )
            )
        )
    });


    verifyOtpFailure$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(AuthActions.verifyOtpFailure),
            tap(({ error }) => {
                this._globalService.toastrError(error);
            })
        )
    }, { dispatch: false });




    verifyOtpSuccess$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(AuthActions.verifyOtpSuccess),
            tap(({ userData }) => {
                this.setLayoutConfig();
                localStorage.removeItem(LocalStorageKeys.IS_2FA_ENABLED);
                this._sharedStoreFacade$.getDocumentTypes();
                this.store.dispatch(AuthActions.fetchUserDataSucceeded({ userData }));
                this.performAuthAction({
                    userData: userData,
                    isThirdPartyLogin: false,
                    formData: null
                });
            })
        )
    }, { dispatch: false });

    iframeableLogin$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(AuthActions.iframeableLogin),
            tap(({ userData, templateId }) => {
                this.setLayoutConfig(true);
                this._sharedStoreFacade$.getDocumentTypes();
                this.store.dispatch(AuthActions.fetchUserDataSucceeded({ userData }));
                this.performAuthAction({
                    userData: userData,
                    isThirdPartyLogin: false,
                    formData: null,
                    templateId: templateId
                });
            })
        )
    }, { dispatch: false });

    iframeableCreateDocument$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(AuthActions.iframeableCreateDocument),
            switchMap(({ templateId }) => {
                return this.iframeAbleService.createDocumentViaTemplateId(templateId).pipe(
                    map((response: { id: number, message: string }) => AuthActions.iframeableCreateDocumentSuccess(response.id)),
                    catchError(error => of(AuthActions.iframeableCreateDocumentFailure(error)))
                )

            })
        )
    });


    iframeableCreateDocumentFailure$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(AuthActions.iframeableCreateDocumentFailure),
            tap(({ error }) => {
                this._globalService.toastrError(error);
                this.redirectToView(false, 'iframeable/select-template');
            })
        )
    }, { dispatch: false });




    iframeableCreateDocumentSuccess$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(AuthActions.iframeableCreateDocumentSuccess),
            tap(({ documentId }) => {
                const redirect = `/send-document/${documentId}`;
                this.redirectToView(false, redirect);
            })
        )
    }, { dispatch: false });


    fetchUserDataSucceeded$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(AuthActions.fetchUserDataSucceeded),
            tap(x => {
                this._globalService.updateUserData(x.userData);
                this._sharedStoreFacade$.saveUserTimezone(x?.userData?.user_timezone);
                const user_settings = this.persistUserRoles(x?.userData);
                this.store.dispatch(new ADD_USER_SETTINGS(user_settings));
                this.store.dispatch(AuthActions.loadAuthData());
                this._sharedStoreFacade$.getDocumentTypes();
                this.store.dispatch(CategoryAction.fetchCategories());
                this._routerService$.setCrmParams(); // If user comes from CRM then load deal data in state
                localStorage.setItem('user', JSON.stringify(user_settings));
            }
            )
        )
    }, {
        dispatch: false
    });


    fetchUserSettings$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(AuthActions.swtichAndRedirectUserSuccess),
            switchMap(() =>
                this.appService.settings().pipe(
                    tap(user_settings => {
                        user_settings = this.persistUserRoles(user_settings);
                        this.store.dispatch(new ADD_USER_SETTINGS(user_settings));
                        this.store.dispatch(AuthActions.loadAuthData());
                        this._sharedStoreFacade$.getDocumentTypes();
                        this._sharedStoreFacade$.saveUserTimezone(user_settings?.user_timezone);
                        this.store.dispatch(CategoryAction.fetchCategories());
                        this._routerService$.setCrmParams(); // If user comes from CRM then load deal data in state
                        localStorage.setItem(LocalStorageKeys.USER, JSON.stringify(user_settings));
                        this._addonPermissionService$.detectAddonPermission();
                    }),
                    catchError(error => of(AuthActions.fetchUserSettingsFailed(error)))
                )
            )
        )
    }, { dispatch: false });


    refreshSession$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(AuthActions.refreshSession),
            switchMap(() => this.authService.refreshSession()
                .pipe(
                    map(() => AuthActions.refreshSessionSuccess()),
                    catchError(error => of(AuthActions.refreshSessionFailed({ error })))
                )
            ))
    });

    refreshSessionSucceeded$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(AuthActions.refreshSessionSuccess),
            tap(() => {
                this.store.dispatch(AuthActions.scheduleRefreshSession());
            }))

    }, { dispatch: false });
    refreshSessionScheduled = createEffect(() => {
        return this.actions$.pipe(
            ofType(AuthActions.scheduleRefreshSession),
            map(() => {
                const calculated_value = (this.calculateTimeToExpire() - this.REFRESH_SESSION_BEFORE); //Calculate when we should start detecting user inactivity
                return calculated_value > 0
                    ? calculated_value
                    : 60 * 1000;
            }),
            switchMap((delayWindow) => timer(delayWindow / 2)),//Make some time to detect user inactivity
            switchMap(() => this._idleUserDetectionService.userInactive
                .pipe(
                    filter(isInactive => isInactive === false),// react only if user is active
                    map(() => {
                        // Since session can be prolonged on server side we have to validate if there is a real need to extend it manually from FE
                        const isAboutToExpire = this.calculateTimeToExpire() < this.REFRESH_SESSION_BEFORE;
                        if (isAboutToExpire) {
                            return AuthActions.refreshSession();
                        } else {
                            return AuthActions.scheduleRefreshSession();
                        }
                    }),
                    take(1)
                )

            ),

        )
    });

    /**
     * While active on the editor routes (edit-template, send-document, edit-content) ping keep-authenticated
     * every 10 minutes regardless of remember-me/idle state, so in-progress editor work isn't lost to session expiry.
     */
    editorSessionKeepAlive$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(AuthActions.startEditorSessionKeepAlive),
            switchMap(() => of(null).pipe(
                delay(10 * 60 * 1000),
                expand(() => this.actions$.pipe(
                    ofType(AuthActions.refreshSessionSuccess, AuthActions.refreshSessionFailed),
                    take(1),
                    delay(10 * 60 * 1000)
                )),
                takeUntil(this.actions$.pipe(ofType(AuthActions.stopEditorSessionKeepAlive)))
            )),
            map(() => AuthActions.refreshSession())
        )
    });

    startUserInactivityDetection$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(UserActions.UserSettingsActionTypes.START_USER_INACTIVITY_DETECTION),
            tap(() => {
                this._idleUserDetectionService.startDetection()
            }));
    }, {
        dispatch: false
    })

    stopUserInactivityDetection$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(UserActions.UserSettingsActionTypes.STOP_USER_INACTIVITY_DETECTION),
            tap(() => {
                this._idleUserDetectionService.stopDetections()
            })
        )
    }, {
        dispatch: false
    });


    swtichAndRedirectUser$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(AuthActions.swtichAndRedirectUser),
            switchMap(action => {
                const isExternalLogin = action.key === 'external_login';
                const apiCall = isExternalLogin
                    ? this.authService.getSessionFromTokenGeneral(action.token)
                    : this.authService.getSessionFromToken(action.token);
                return apiCall.pipe(
                    map(userData =>
                        AuthActions.swtichAndRedirectUserSuccess({ userData })
                    ),
                    catchError(() => {
                        this._router.navigate(['login']);
                        return of(AuthActions.swtichAndRedirectUserFail());
                    })
                );
            })
        );
    });



    swtichAndRedirectUserSuccess$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(AuthActions.swtichAndRedirectUserSuccess),
            map(({ userData }) => AuthActions.loginSuccess({
                userData: userData,
                formData: null,
                isThirdPartyLogin: false
            }))
        )
    })

    // inside AuthEffects class (add near other effects)
    ssoLogin$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AuthActions.ssoLogin),

            // keep same behaviour as login: start user inactivity detection (optional)
            tap(() => {
            this.store.dispatch(new UserActions.START_USER_INACTIVITY_DETECTION());
            }),

            mergeMap(action => {
            // ensure storage access (same as login flow)
            return from(this._storageAccessManager.ensureStorageAccess()).pipe(
                mergeMap(() => {
                return this.authService.initiateSsoLogin(action.email).pipe(
                    map((res : {authorization_url: string, error: string}) => {
                    // Expect res { url } or { error }
                    if (res && res.authorization_url) {
                        return AuthActions.ssoLoginSuccess({ url: res.authorization_url });
                    } else {
                        return AuthActions.ssoLoginFailure({ error: res?.error || translate('Something went wrong (general toast error) - Something went wrong') });
                    }
                    }),
                    catchError(error => of(AuthActions.ssoLoginFailure({ error })))
                );
                })
            );
            })
        )
    );

    ssoLoginSuccess$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AuthActions.ssoLoginSuccess),
            tap(({ url }) => {
            if (url) {
                // directly redirect browser to SSO provider URL
                window.location.href = url;
            }
            })
        ),
    { dispatch: false });


    private async performAuthAction(action) {
        const loginData = action.userData // Assuming your user data is structured appropriately
        this.authService.saveData(LocalStorageKeys.USER, JSON.stringify(loginData));
        this.eventService.emitEvent({ data: loginData.data ?? {} });
        this._store$.dispatch(AuthActions.fetchUserData());
        this._store$.dispatch(AuthActions.hubspotTrackingData());
        this.redirectUser(action);
    }


    /**
     * Redirect user behalf on authentication request
     * @param action
     */
    private redirectUser(action) {
        let redirect = decodeURIComponent(this._route.snapshot.queryParamMap.get('returnUrl') || '/');
        const { isThirdPartyLogin, templateId, userData } = action;
        if (!isThirdPartyLogin && userData?.loginAttempt?.login_via !== LoginVia.IFRAMEABLE) {
            this._storageService$.removeItem('crmData');
            this._storageService$.removeItem('is_crm_user');
            this.setLayoutConfig();
        }
        else if (userData?.loginAttempt?.login_via === LoginVia.IFRAMEABLE) {
            if (templateId) {
                this.store.dispatch(AuthActions.iframeableCreateDocument(templateId));
                return;
            }
            redirect = 'iframeable/select-template';
            this.redirectToView(false, redirect);
            return;
        }
        else {
            const crmData = JSON.parse(this._storageService$.getItem('crmData') || '{}');
            this.store.dispatch(AuthActions.loadCrmData(crmData));
        }
        this.redirectToView(isThirdPartyLogin, redirect);
    }

    private redirectToView(isThirdPartyLogin: boolean, redirect: string) {
        this._routerService$.redirectCrmUserToRoute(isThirdPartyLogin ? '/integrations/select-template' : redirect);
    }

    private setLayoutConfig(hideLayout: boolean = false, customizer: boolean = true, enableLocalStorage: boolean = true): void {
        this.authHelpder.setLayoutConfig(hideLayout, customizer, enableLocalStorage);
    }

    private persistUserRoles(user_settings: UserSettings) {
        if (user_settings?.roles) {
            return user_settings;
        }

        const userFromLS = JSON.parse(localStorage.getItem('user'));

        return ({
            ...user_settings,
            roles: userFromLS?.roles
        });
    }

    // returns how many seconds left for session expiration
    private calculateTimeToExpire(): number {
        const expire_timestamp = Number(localStorage.getItem(LocalStorageKeys.SESSION_EXPIRE_AT)) || 0;
        return ((expire_timestamp) - (moment.utc().valueOf() / 1000));
    }


}